import { defineStore } from 'pinia'
import {
  getCompanySettings,
  updateCompanySettings,
  type CompanySettingsType
} from '@/services/company'
import { useEmployeeStore } from '@/stores/employee'
import { logger } from '@/services/logger'
import { isAxiosError } from 'axios'
import { computed, ref } from 'vue'
import { isOnboardingType } from '@/helpers/onboarding-helpers'

export enum ONBOARDING_VERSION_ENUM {
  V1 = 1
}

const STEP_ORDER: (keyof OnboardingStepsCollectionType)[] = [
  'add_domain_name',
  'add_collaborator',
  'upload_travel_policies'
]

export interface OnboardingStepsCollectionType {
  add_domain_name: boolean
  add_collaborator: boolean
  upload_travel_policies: boolean
}

export interface OnboardingType {
  version: ONBOARDING_VERSION_ENUM
  steps: OnboardingStepsCollectionType
}

const defaultCompanySettings: OnboardingType = {
  version: ONBOARDING_VERSION_ENUM.V1,
  steps: {
    add_domain_name: false,
    add_collaborator: false,
    upload_travel_policies: false
  }
}

export const useCompanySettingsStore = defineStore('companySettings', () => {
  const companySettings = ref<CompanySettingsType>({})

  const resolveCompanyId = async (): Promise<string> => {
    const employeeStore = useEmployeeStore()

    if (!employeeStore.employee?.companyId) {
      await employeeStore.setEmployee()
    }

    const companyId = employeeStore.employee?.companyId
    if (!companyId) {
      throw new Error('No company Id')
    }

    return companyId
  }

  const updateOnboardingData = async (
    companyId: string,
    onboardingData: OnboardingType
  ): Promise<void> => {
    try {
      const updatedPayload = {
        ...companySettings.value,
        onboarding: onboardingData
      }

      await updateCompanySettings(companyId, updatedPayload)
      companySettings.value = updatedPayload
    } catch (error) {
      logger.error(error)

      throw error
    }
  }

  const setCompanySettings = async () => {
    try {
      const companyId = await resolveCompanyId()
      const { data } = await getCompanySettings(companyId)

      companySettings.value = data
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        const companyId = await resolveCompanyId()
        await updateOnboardingData(companyId, defaultCompanySettings)
        return
      }

      logger.error(error)

      throw error
    }
  }

  const onBoarding = computed<OnboardingType | undefined>(() => {
    const onboarding = companySettings.value.onboarding

    if (!isOnboardingType(onboarding)) {
      return undefined
    }

    const orderedSteps = STEP_ORDER.reduce<OnboardingStepsCollectionType>(
      (acc, key) => {
        acc[key] = onboarding.steps[key]

        return acc
      },
      { ...defaultCompanySettings.steps }
    )

    return {
      version: onboarding.version,
      steps: orderedSteps
    }
  })

  return {
    companySettings,
    onBoarding,
    setCompanySettings,
    updateOnboardingData
  }
})
